/**
 * TemplateQuestionsEditor Component
 * 
 * Reusable questions editor for audit templates.
 * Used in both AI Template Builder and Template Editor Page.
 * Provides full CRUD operations for questions, answer options, scores, and action items.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Type definitions
export interface QuestionOption {
  id?: number;
  option_text: string;
  option_seq: number;
  score?: number;
  action_item?: string;
}

export interface Question {
  id?: number;
  sequence: number;
  level01: string;
  level02: string;
  level03: string;
  level04: string;
  level05: string;
  description: string;
  mandatory_beneficial: string;
  question_type: string;
  short_text?: string;
  long_text?: string;
  options: QuestionOption[];
}

interface TemplateQuestionsEditorProps {
  questions: Question[];
  onChange: (questions: Question[]) => void;
  isReadOnly?: boolean;
  loading?: boolean;
  onSave?: () => Promise<void>;
  showSaveButton?: boolean;
}

const UNCATEGORIZED_HEADING = 'Uncategorized';
const GENERAL_HEADING = 'General';

/** Display name used for grouping empty level 1 headings. */
const getLevel1Heading = (question: Question): string => question.level01 || UNCATEGORIZED_HEADING;

/** Display name used for grouping empty level 2 headings. */
const getLevel2Heading = (question: Question): string => question.level02 || GENERAL_HEADING;

const TemplateQuestionsEditor: React.FC<TemplateQuestionsEditorProps> = ({
  questions,
  onChange,
  isReadOnly = false,
  loading = false,
  onSave,
  showSaveButton = false,
}) => {
  // State for question editing dialog
  const [questionDialog, setQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editingHeading, setEditingHeading] = useState<{
    level: 1 | 2;
    level1: string;
    level2?: string;
    value: string;
  } | null>(null);

  /**
   * Add a new question with default values
   */
  const handleAddQuestion = () => {
    const newQuestion: Question = {
      sequence: questions.length + 1,
      level01: '',
      level02: '',
      level03: '',
      level04: '',
      level05: '',
      description: '',
      mandatory_beneficial: 'Beneficial',
      question_type: 'Multichoice',
      options: [
        { option_text: 'Poor', option_seq: 1, score: 0 },
        { option_text: 'Fair', option_seq: 2, score: 50 },
        { option_text: 'Good', option_seq: 3, score: 100 },
      ]
    };
    setEditingQuestion(newQuestion);
    setQuestionDialog(true);
  };

  /**
   * Open edit dialog for existing question
   */
  const handleEditQuestion = (question: Question) => {
    setEditingQuestion({ ...question });
    setQuestionDialog(true);
  };

  /**
   * Save question (add or update)
   */
  const handleSaveQuestion = () => {
    if (!editingQuestion) return;
    
    if (editingQuestion.id) {
      // Update existing question
      onChange(questions.map(q => 
        q.id === editingQuestion.id ? editingQuestion : q
      ));
    } else {
      // Add new question with generated ID
      onChange([...questions, { ...editingQuestion, id: Date.now() }]);
    }
    
    setQuestionDialog(false);
    setEditingQuestion(null);
  };

  /**
   * Delete a question by ID
   */
  const handleDeleteQuestion = (questionId: number) => {
    onChange(questions.filter(q => q.id !== questionId));
  };

  const startHeadingEdit = (level: 1 | 2, level1: string, level2?: string) => {
    setEditingHeading({
      level,
      level1,
      level2,
      value: level === 1 ? level1 : (level2 || ''),
    });
  };

  const saveHeadingEdit = () => {
    if (!editingHeading) return;
    const nextName = editingHeading.value.trim();
    const currentName = editingHeading.level === 1
      ? editingHeading.level1
      : (editingHeading.level2 || '');
    // Leave blank headings empty unless the user actually changes the label.
    if (!nextName || nextName === currentName) {
      setEditingHeading(null);
      return;
    }

    if (editingHeading.level === 1) {
      onChange(questions.map((question) => (
        getLevel1Heading(question) === editingHeading.level1
          ? { ...question, level01: nextName }
          : question
      )));
    } else {
      onChange(questions.map((question) => (
        getLevel1Heading(question) === editingHeading.level1
          && getLevel2Heading(question) === editingHeading.level2
          ? { ...question, level02: nextName }
          : question
      )));
    }
    setEditingHeading(null);
  };

  const isEditingHeading = (level: 1 | 2, level1: string, level2?: string): boolean => {
    if (!editingHeading || editingHeading.level !== level) return false;
    if (editingHeading.level1 !== level1) return false;
    return level === 1 || editingHeading.level2 === level2;
  };

  /**
   * Update answer option inline (text, score, or action item)
   */
  const updateQuestionOption = (
    questionIndex: number, 
    optionIndex: number, 
    field: keyof QuestionOption, 
    value: string
  ) => {
    const updatedQuestions = [...questions];
    const question = { ...updatedQuestions[questionIndex] };
    const options = [...question.options];
    
    // Convert to number for score field, clamp between 0-100
    if (field === 'score') {
      const numValue = Math.max(0, Math.min(100, parseInt(value) || 0));
      options[optionIndex] = { ...options[optionIndex], [field]: numValue };
    } else {
      options[optionIndex] = { ...options[optionIndex], [field]: value };
    }
    
    question.options = options;
    updatedQuestions[questionIndex] = question;
    onChange(updatedQuestions);
  };

  // Group questions by Category Level 1 and Level 2
  const groupedQuestions = React.useMemo(() => {
    const groups: { [key: string]: { [key: string]: Question[] } } = {};
    
    questions.forEach((question) => {
      const level1 = getLevel1Heading(question);
      const level2 = getLevel2Heading(question);
      
      if (!groups[level1]) {
        groups[level1] = {};
      }
      if (!groups[level1][level2]) {
        groups[level1][level2] = [];
      }
      groups[level1][level2].push(question);
    });
    
    return groups;
  }, [questions]);

  return (
    <Box>
      {/* Header with question count and action buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">
          Questions & Answer Options ({questions.length})
        </Typography>
        {!isReadOnly && (
          <Box display="flex" gap={2}>
            {showSaveButton && onSave && (
              <Button
                variant="outlined"
                size="small"
                onClick={onSave}
                disabled={loading}
              >
                Save Questions
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddQuestion}
            >
              Add Question
            </Button>
          </Box>
        )}
      </Box>

      {/* Loading state */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : questions.length === 0 ? (
        /* Empty state */
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Questions Added Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start building your audit by adding questions and answer options
            </Typography>
            {!isReadOnly && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddQuestion}
              >
                Add First Question
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Questions list grouped by categories */
        Object.entries(groupedQuestions).map(([level1, level2Groups]) => (
          <Box key={level1} sx={{ mb: 4 }}>
            {/* Category Level 1 Header */}
            <Box sx={{ 
              mb: 2, 
              pb: 1, 
              borderBottom: 2,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              {isEditingHeading(1, level1) ? (
                <>
                  <TextField
                    size="small"
                    value={editingHeading?.value || ''}
                    onChange={(e) => setEditingHeading((prev) => prev ? { ...prev, value: e.target.value } : prev)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveHeadingEdit();
                      if (e.key === 'Escape') setEditingHeading(null);
                    }}
                    autoFocus
                    fullWidth
                    inputProps={{ 'aria-label': 'Level 1 heading' }}
                  />
                  <IconButton size="small" onClick={saveHeadingEdit} aria-label="Save heading">
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditingHeading(null)} aria-label="Cancel heading edit">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {level1}
                  </Typography>
                  {!isReadOnly && (
                    <IconButton
                      size="small"
                      onClick={() => startHeadingEdit(1, level1)}
                      aria-label={`Edit ${level1} heading`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
            
            {/* Category Level 2 Groups */}
            {Object.entries(level2Groups).map(([level2, questionsInGroup]) => (
              <Box key={`${level1}-${level2}`} sx={{ mb: 3 }}>
                {/* Category Level 2 Header */}
                <Box sx={{ 
                  mb: 1.5, 
                  pb: 0.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  ml: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  {isEditingHeading(2, level1, level2) ? (
                    <>
                      <TextField
                        size="small"
                        value={editingHeading?.value || ''}
                        onChange={(e) => setEditingHeading((prev) => prev ? { ...prev, value: e.target.value } : prev)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveHeadingEdit();
                          if (e.key === 'Escape') setEditingHeading(null);
                        }}
                        autoFocus
                        fullWidth
                        inputProps={{ 'aria-label': 'Level 2 heading' }}
                      />
                      <IconButton size="small" onClick={saveHeadingEdit} aria-label="Save heading">
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setEditingHeading(null)} aria-label="Cancel heading edit">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        {level2}
                      </Typography>
                      {!isReadOnly && (
                        <IconButton
                          size="small"
                          onClick={() => startHeadingEdit(2, level1, level2)}
                          aria-label={`Edit ${level2} heading`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  )}
                </Box>
                
                {/* Questions in this group */}
                {questionsInGroup.map((question) => {
                  const index = questions.findIndex(q => q.id === question.id || q.sequence === question.sequence);
                  return (
                    <Accordion key={question.id || index} sx={{ mb: 1, ml: 4 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box display="flex" alignItems="center" width="100%">
                          <DragIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1">
                              {question.sequence}. {question.description || 'Untitled Question'}
                            </Typography>
                          </Box>
                          <Chip 
                            size="small" 
                            label={question.mandatory_beneficial}
                            color={question.mandatory_beneficial === 'Mandatory' ? 'error' : 'default'}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Header Row */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 100px 1fr', gap: 2, mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Answer Options
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', textAlign: 'center' }}>
                            Score
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            Action Items
                          </Typography>
                        </Box>
                        
                        {/* Answer Options, Score, and Action Items Grid */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {question.options.map((option, optIndex) => (
                            <Box key={optIndex} sx={{ display: 'grid', gridTemplateColumns: '1fr 100px 1fr', gap: 2, alignItems: 'center' }}>
                              {/* Answer Option */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ minWidth: 20, fontWeight: 'medium' }}>
                                  {option.option_seq}.
                                </Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={option.option_text}
                                  onChange={(e) => updateQuestionOption(index, optIndex, 'option_text', e.target.value)}
                                  variant="outlined"
                                  disabled={isReadOnly}
                                  sx={{ 
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50'
                                    }
                                  }}
                                />
                              </Box>
                              
                              {/* Score */}
                              <TextField
                                size="small"
                                type="number"
                                value={option.score ?? 0}
                                onChange={(e) => updateQuestionOption(index, optIndex, 'score', e.target.value)}
                                variant="outlined"
                                disabled={isReadOnly}
                                inputProps={{ min: 0, max: 100, step: 1 }}
                                sx={{ 
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: (theme) => {
                                      const score = option.score ?? 0;
                                      if (theme.palette.mode === 'dark') {
                                        // Dark mode: use subtle tints
                                        return score >= 80 ? 'rgba(34, 197, 94, 0.15)' : 
                                               score >= 50 ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.15)';
                                      } else {
                                        // Light mode: use existing colors
                                        return score >= 80 ? '#dcfce7' : 
                                               score >= 50 ? '#fef9c3' : '#fee2e2';
                                      }
                                    }
                                  }
                                }}
                              />
                              
                              {/* Action Item */}
                              <TextField
                                fullWidth
                                size="small"
                                value={option.action_item || ''}
                                onChange={(e) => updateQuestionOption(index, optIndex, 'action_item', e.target.value)}
                                placeholder="Add action item (optional)"
                                variant="outlined"
                                disabled={isReadOnly}
                                multiline
                                maxRows={3}
                                sx={{ 
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50'
                                  }
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                        
                        {/* Edit/Delete Actions */}
                        {!isReadOnly && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditQuestion(question)}
                            >
                              Edit Question
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => question.id && handleDeleteQuestion(question.id)}
                            >
                              Delete
                            </Button>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            ))}
          </Box>
        ))
      )}

      {/* Question Edit Dialog */}
      <Dialog
        open={questionDialog}
        onClose={() => setQuestionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingQuestion?.id ? 'Edit Question' : 'Add New Question'}
        </DialogTitle>
        <DialogContent>
          {editingQuestion && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Question Description */}
                <TextField
                  fullWidth
                  label="Question Description"
                  value={editingQuestion.description}
                  onChange={(e) => setEditingQuestion(prev => prev ? 
                    { ...prev, description: e.target.value } : null
                  )}
                  placeholder="What is your question?"
                />
                
                {/* Category Levels */}
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    fullWidth
                    label="Category Level 1"
                    value={editingQuestion.level01}
                    onChange={(e) => setEditingQuestion(prev => prev ? 
                      { ...prev, level01: e.target.value } : null
                    )}
                    placeholder="e.g., Operations"
                  />
                  <TextField
                    fullWidth
                    label="Category Level 2"
                    value={editingQuestion.level02}
                    onChange={(e) => setEditingQuestion(prev => prev ? 
                      { ...prev, level02: e.target.value } : null
                    )}
                    placeholder="e.g., Inventory Management"
                  />
                </Box>
                
                {/* Question Type and Priority */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Question Type</InputLabel>
                    <Select
                      value={editingQuestion.question_type}
                      onChange={(e) => setEditingQuestion(prev => prev ? 
                        { ...prev, question_type: e.target.value } : null
                      )}
                      label="Question Type"
                    >
                      <MenuItem value="Multichoice">Multiple Choice</MenuItem>
                      <MenuItem value="Text">Text Input</MenuItem>
                      <MenuItem value="Number">Number Input</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={editingQuestion.mandatory_beneficial}
                      onChange={(e) => setEditingQuestion(prev => prev ? 
                        { ...prev, mandatory_beneficial: e.target.value } : null
                      )}
                      label="Priority"
                    >
                      <MenuItem value="Mandatory">Mandatory</MenuItem>
                      <MenuItem value="Beneficial">Beneficial</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              
              {/* Answer Options Section - Show for Multiple Choice questions */}
              {editingQuestion.question_type === 'Multichoice' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Answer Options:
                  </Typography>
                  {editingQuestion.options && editingQuestion.options.map((option, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        size="small"
                        label={`Option ${option.option_seq}`}
                        value={option.option_text}
                        onChange={(e) => {
                          const updatedOptions = [...editingQuestion.options];
                          updatedOptions[index].option_text = e.target.value;
                          setEditingQuestion(prev => prev ? 
                            { ...prev, options: updatedOptions } : null
                          );
                        }}
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        color="error"
                        onClick={() => {
                          const updatedOptions = editingQuestion.options.filter((_, i) => i !== index);
                          setEditingQuestion(prev => prev ? 
                            { ...prev, options: updatedOptions } : null
                          );
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newOption: QuestionOption = {
                        option_text: '',
                        option_seq: editingQuestion.options.length + 1
                      };
                      setEditingQuestion(prev => prev ? 
                        { ...prev, options: [...prev.options, newOption] } : null
                      );
                    }}
                  >
                    Add Option
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuestionDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveQuestion}
            disabled={!editingQuestion?.description.trim()}
          >
            Save Question
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateQuestionsEditor;

